import * as React from "react";
import {
  AppMark,
  Avatar,
  Boton,
  Campo,
  Card,
  CardContenido,
  CardDescripcion,
  CardEncabezado,
  CardTitulo,
  Dialog,
  DialogContenido,
  DialogDescripcion,
  DialogDisparador,
  DialogEncabezado,
  DialogPie,
  DialogTitulo,
  Esqueleto,
  EsqueletoTabla,
  Input,
  Pill,
  PuntoNaranja,
  Select,
  SelectContenido,
  SelectDisparador,
  SelectOpcion,
  SelectValor,
  Semaforo,
  Sheet,
  SheetContenido,
  SheetDisparador,
  SheetTitulo,
  Tabla,
  TablaCabecera,
  TablaCelda,
  TablaCuerpo,
  TablaEncabezado,
  TablaFila,
  Tabs,
  TabsContenido,
  TabsDisparador,
  TabsLista,
  Textarea,
  TituloPagina,
  useToast,
  Vacio,
  Wordmark,
} from "@iavmx/ui";

function Bloque({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="font-titulo text-lg font-bold">{titulo}</h2>
      <div className="flex flex-wrap items-center gap-3">{children}</div>
    </section>
  );
}

const paleta: [string, string][] = [
  ["cream", "bg-fondo"],
  ["suave", "bg-fondo-suave"],
  ["alzado", "bg-fondo-alzado"],
  ["tinta", "bg-tinta"],
  ["naranja", "bg-naranja"],
  ["azul", "bg-azul"],
  ["verde", "bg-verde"],
  ["ámbar", "bg-ambar"],
  ["rojo", "bg-rojo"],
];

/** La vitrina de @iavmx/ui: todo lo que reciben las apps, con el sistema de Vera. */
export function Vitrina() {
  const { avisar } = useToast();
  return (
    <>
      <TituloPagina descripcion="Componentes de @iavmx/ui con el sistema visual de Vera. Cream, tinta, naranja como único acento, esquinas rectas.">
        Componentes
        <PuntoNaranja />
      </TituloPagina>
      <div className="space-y-10">
        <Bloque titulo="Marca">
          <Wordmark tamano="text-5xl" />
          <Wordmark tamano="text-3xl" />
          <AppMark />
          <img src="/marca/vera-wordmark.png" alt="Wordmark oficial" className="h-8" />
        </Bloque>
        <Bloque titulo="Tipografía">
          <div className="space-y-1">
            <p className="font-titulo text-3xl font-black">Bitter Black para títulos</p>
            <p className="font-titulo text-xl font-bold">Bitter Bold para subtítulos</p>
            <p className="font-ui text-base">Blinker Regular para la interfaz y el cuerpo.</p>
            <p className="font-ui text-sm font-semibold">
              Blinker SemiBold para énfasis y botones.
            </p>
          </div>
        </Bloque>
        <Bloque titulo="Color">
          {paleta.map(([n, c]) => (
            <div key={n} className="flex flex-col items-center gap-1">
              <div className={`size-12 border border-linea ${c}`} />
              <span className="font-ui text-xs text-tinta-tenue">{n}</span>
            </div>
          ))}
        </Bloque>
        <Bloque titulo="Botones">
          <Boton>Primario</Boton>
          <Boton variante="acento">Acento (uno por pantalla)</Boton>
          <Boton variante="secundario">Secundario</Boton>
          <Boton variante="contorno">Contorno</Boton>
          <Boton variante="fantasma">Fantasma</Boton>
          <Boton variante="liga">Liga</Boton>
          <Boton variante="peligro">Peligro</Boton>
          <Boton disabled>Deshabilitado</Boton>
          <Boton tamano="sm">Chico</Boton>
          <Boton tamano="lg">Grande</Boton>
        </Bloque>
        <Bloque titulo="Formulario">
          <div className="grid w-full max-w-lg gap-4">
            <Campo etiqueta="Objetivo" ayuda="Una línea: qué quieres lograr." htmlFor="obj">
              <Input id="obj" placeholder="Anunciar el aniversario del 4 de octubre" />
            </Campo>
            <Campo etiqueta="Área responsable" htmlFor="area">
              <Select>
                <SelectDisparador id="area">
                  <SelectValor placeholder="Elige un área" />
                </SelectDisparador>
                <SelectContenido>
                  <SelectOpcion value="mercadotecnia">Mercadotecnia</SelectOpcion>
                  <SelectOpcion value="multimedia">Multimedia</SelectOpcion>
                  <SelectOpcion value="editorial">Editorial</SelectOpcion>
                </SelectContenido>
              </Select>
            </Campo>
            <Campo etiqueta="Mensaje original" error="Pega el mensaje completo." htmlFor="msg">
              <Textarea id="msg" placeholder="Lo que llegó por WhatsApp o Slack" />
            </Campo>
          </div>
        </Bloque>
        <Bloque titulo="Pills y semáforo">
          <Pill>Neutro</Pill>
          <Pill tono="tinta">Tinta</Pill>
          <Pill tono="azul">Azul</Pill>
          <Pill tono="naranja">Naranja</Pill>
          <Semaforo color="verde">A tiempo</Semaforo>
          <Semaforo color="ambar">Por vencer</Semaforo>
          <Semaforo color="rojo">Retrasado</Semaforo>
          <Semaforo color="gris">Sin fecha</Semaforo>
        </Bloque>
        <Bloque titulo="Tabla">
          <Tabla>
            <TablaCabecera>
              <TablaFila>
                <TablaEncabezado>Campaña</TablaEncabezado>
                <TablaEncabezado>Área</TablaEncabezado>
                <TablaEncabezado>Fecha límite</TablaEncabezado>
                <TablaEncabezado>Estado</TablaEncabezado>
              </TablaFila>
            </TablaCabecera>
            <TablaCuerpo>
              <TablaFila>
                <TablaCelda>Aniversario 4 de octubre</TablaCelda>
                <TablaCelda>Multimedia</TablaCelda>
                <TablaCelda>30 sep 2026</TablaCelda>
                <TablaCelda>
                  <Semaforo color="ambar">En proceso</Semaforo>
                </TablaCelda>
              </TablaFila>
              <TablaFila>
                <TablaCelda>Suplemento Rodeo Capital</TablaCelda>
                <TablaCelda>Editorial</TablaCelda>
                <TablaCelda>15 oct 2026</TablaCelda>
                <TablaCelda>
                  <Semaforo color="verde">Terminado</Semaforo>
                </TablaCelda>
              </TablaFila>
            </TablaCuerpo>
          </Tabla>
        </Bloque>
        <Bloque titulo="Tarjeta, avatar">
          <Card className="max-w-sm">
            <CardEncabezado>
              <CardTitulo>Tarjeta sin radio</CardTitulo>
              <CardDescripcion>Hairline, fondo cream, sin sombra.</CardDescripcion>
            </CardEncabezado>
            <CardContenido className="flex items-center gap-3">
              <Avatar nombre="Nailea Aguirre" />
              <Avatar nombre="Eli Vázquez" />
              <Avatar nombre="Bianca Aray" />
            </CardContenido>
          </Card>
        </Bloque>
        <Bloque titulo="Tabs">
          <Tabs defaultValue="campanas" className="w-full">
            <TabsLista>
              <TabsDisparador value="campanas">Campañas</TabsDisparador>
              <TabsDisparador value="tickets">Tickets</TabsDisparador>
            </TabsLista>
            <TabsContenido value="campanas">Vista de Mercadotecnia.</TabsContenido>
            <TabsContenido value="tickets">Vista general del Semáforo.</TabsContenido>
          </Tabs>
        </Bloque>
        <Bloque titulo="Dialog, sheet, toast">
          <Dialog>
            <DialogDisparador asChild>
              <Boton variante="contorno">Abrir dialog</Boton>
            </DialogDisparador>
            <DialogContenido>
              <DialogEncabezado>
                <DialogTitulo>¿Aprobar el desglose de tareas?</DialogTitulo>
                <DialogDescripcion>
                  La IA propuso 6 tareas para 3 áreas. Tú decides.
                </DialogDescripcion>
              </DialogEncabezado>
              <DialogPie>
                <Boton variante="fantasma">Rechazar</Boton>
                <Boton variante="acento">Aprobar</Boton>
              </DialogPie>
            </DialogContenido>
          </Dialog>
          <Sheet>
            <SheetDisparador asChild>
              <Boton variante="contorno">Abrir sheet</Boton>
            </SheetDisparador>
            <SheetContenido>
              <SheetTitulo>Detalle de la solicitud</SheetTitulo>
              <p className="font-ui text-sm text-tinta-2">
                Un panel lateral para editar sin salir de la lista.
              </p>
            </SheetContenido>
          </Sheet>
          <Boton
            variante="secundario"
            onClick={() =>
              avisar({
                titulo: "Guardado",
                descripcion: "La solicitud quedó registrada.",
                tono: "exito",
              })
            }
          >
            Lanzar toast
          </Boton>
        </Bloque>
        <Bloque titulo="Estados">
          <div className="w-full space-y-4">
            <Vacio
              titulo="Sin campañas"
              texto="Cuando Mercadotecnia registre la primera, aparece aquí."
              accion={<Boton variante="acento">Nueva solicitud</Boton>}
            />
            <div className="flex gap-3">
              <Esqueleto className="h-24 w-40" />
              <div className="flex-1">
                <EsqueletoTabla filas={3} />
              </div>
            </div>
          </div>
        </Bloque>
      </div>
    </>
  );
}
